<template>
  <div
    role="region"
    tabindex="-1"
    class='video-wrapper'
    :aria-label="ariaToRead"
    @mousemove="handleMouseMove"
    @mouseleave="showPause = false"
  >
    <client-only>
      <vue-vimeo-player
        class="vimeo"
        ref="vimeo"
        v-if="id"
        :video-url="url"
        :video-id="id"
        :autoplay="autoplay"
        :loop="loop"
        :controls="defaultControls && !customControls"
        :options="options"
        v-on="$listeners"
        @ready="handleReady"
        @error="handleError"
        @ended="handleEnded"
      />
    </client-only>
    <div
      v-if="showCustomControls"
      class="custom-controls"
      :style="placeholderStyle"
      @click="handleControlsClick"
    >
      <button v-show="!isPlaying" class="custom-button" :aria-label="$t('HERO_VIDEO_PLAY_BUTTON')">
        <sh-icon class="custom-icon" name="playVideo" size="xxlarge"/>
        <label class="custom-label">{{ playLabel || $t('HERO_VIDEO_PLAY_BUTTON') }}</label>
      </button>
      <button v-show="showPause" class="custom-button" :aria-label="$t('HERO_VIDEO_PAUSE_BUTTON')">
        <sh-icon class="custom-icon" name="pauseVideo" size="xxlarge"/>
        <label class="custom-label">{{ $t('HERO_VIDEO_PAUSE_BUTTON') }}</label>
      </button>
    </div>
  </div>
</template>

<script>
import { ShIcon } from '@sohohouse/design-system'
import { vueVimeoPlayer } from 'vue-vimeo-player'

// regex should be present for UrL handling
// eslint-disable-next-line no-useless-escape
const vimeoUrlIdRegExp = /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/

export default {
  name: 'VimeoVideo',
  components: {
    ShIcon,
    vueVimeoPlayer
  },
  props: {
    url: {
      type: String,
      required: true
    },
    autoplay: {
      type: Boolean,
      required: false,
      default: false
    },
    loop: {
      type: Boolean,
      required: false,
      default: false
    },
    mute: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultControls: {
      type: Boolean,
      required: false,
      default: true
    },
    customControls: {
      type: Boolean,
      required: false,
      default: false
    },
    hiddenText: {
      type: String,
      required: true
    },
    playerOpts: {
      type: Object,
      default: () => ({})
    },
    placeholder: {
      type: String
    },
    playLabel: {
      type: [String, Boolean],
      validate: pl => pl !== true
    },
    skipToTime: {
      type: Number
    }
  },
  data () {
    return {
      isPlaying: false,
      hasPlayed: false,
      showPause: false,
      pauseTimer: 0
    }
  },
  computed: {
    id () {
      const matches = this.url.match(vimeoUrlIdRegExp)
      if (matches && matches[3]) {
        return matches[3]
      }
      return ''
    },
    ariaToRead () {
      return `${this.$t('VIDEO_PLAYER_LABEL')} ${this.hiddenText}`
    },
    options () {
      // blocked the player from tracking any session data in the autoplay mode
      const dntOpts = this.autoplay ? { dnt: true } : {}
      return { ...this.playerOpts, ...dntOpts }
    },
    showCustomControls () {
      if (this.customControls) return true
      if (this.placeholder) return !this.hasPlayed
      return false
    },
    placeholderStyle () {
      if (!this.placeholder || this.isPlaying || (!this.customControls && this.hasPlayed)) return {}
      return { backgroundImage: `url(${this.placeholder})` }
    },
    isMobile () {
      return this.$device.isMobile
    }
  },
  methods: {
    async handleReady () {
      if (this.mute) {
        this.$refs.vimeo.mute()
      }

      if (this.autoplay) {
        this.isPlaying = true
      }

      if (this.skipToTime && !Number.isNaN(this.skipToTime)) {
        const { player } = this.$refs.vimeo
        const duration = await player.getDuration()

        const timeToSkip = Math.min(duration, this.skipToTime)
        this.$refs.vimeo.player.setCurrentTime(timeToSkip)

        if (timeToSkip === duration) this.handleEnded()
      }

      // disabled autopause for allowing autoplay multiple videos
      this.$refs.vimeo.player.setAutopause(false)
    },
    handleEnded () {
      this.isPlaying = false
      this.showPause = false
      this.$emit('ended')
    },
    handleMouseMove () {
      if (this.isPlaying && !this.isMobile) this.pauseEvent()
    },
    pauseEvent () {
      clearTimeout(this.pauseTimer)
      this.showPause = true
      this.pauseTimer = setTimeout(() => {
        this.showPause = false
      }, this.isMobile ? 3000 : 1000)
    },
    handleControlsClick () {
      if (this.isEditMode) return

      if (!this.isPlaying) this.playVideo()
      else if (this.isMobile) {
        // first tap on mobile video being played
        if (!this.showPause) this.pauseEvent()
        else this.pauseVideo()
      } else this.pauseVideo()
    },
    playVideo () {
      this.hasPlayed = true
      this.isPlaying = true
      this.$refs.vimeo.play()
    },
    pauseVideo () {
      this.isPlaying = false
      this.showPause = false
      this.$refs.vimeo.pause()
    },
    handleError (err) {
      this.$emit('error', err)
    }
  }
}
</script>

<style lang="scss" scoped>

.video-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  @include ratio(16, 9);

  .fallback-image {
    &, & img {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center top;
    }
  }

  .vimeo, .vimeo ::v-deep iframe {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    border: 0;
    will-change: auto;
    animation: fade $t*5;
  }

  .custom-controls {
    display: block;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;

    .custom-button {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translateY(-50%) translateX(-50%);
      text-align: center;
      border: 0;
      background: transparent;
      cursor: pointer;

      &.focus-visible {
        outline: 1px solid $color-white;
      }

      .custom-icon {
        margin-bottom: $space-070;
      }

      .custom-label {
        color: $color-white;
        @include font('050')
      }
    }
  }
}
</style>
